import { createScopedLogger, getMimeTypeFromName } from "@@common";
import { Box } from "@mui/material";
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FileClient } from "../../../api/api";
import { AppMediaClient } from "../../../api/AppMediaClient";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import { useApiClient, useErrorHandler } from "../../../common/hooks";
import BundleContext from "../../Contexts/BundleContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { ImageModel } from "../../Models/ImageModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("ImageComponent");

function ImageSurface({ src, alt, layout, selection, pending }: {
  src?: string;
  alt: string;
  layout: ReturnType<typeof getComponentLayout>;
  selection: ReturnType<typeof useComponentSelection>;
  pending: boolean;
}) {
  const image = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useLayoutEffect(() => {
    if (image.current?.complete && image.current.naturalWidth > 0) setLoaded(true);
  }, []);
  if (!src || failed) {
    return <Box component="span" role="img" aria-label={alt || undefined} aria-busy={pending && !failed}
      sx={{ display: "inline-block", ...layout }} {...selection} />;
  }
  return <Box component="img" ref={image} src={src} alt={alt}
    onLoad={() => setLoaded(true)} onError={() => setFailed(true)}
    sx={{ ...layout, ...(!loaded ? { visibility: "hidden" } : {}) }} {...selection} />;
}

const ImageComponent: React.FC<IComponentProps<ImageModel>> = ({ component, context, componentPath = [] }) => {
  const bundle = useContext(BundleContext);
  const errorHandler = useErrorHandler();
  const fileClient = useApiClient(FileClient);
  const mediaClient = useApiClient(AppMediaClient);
  const componentLayoutSx = getComponentLayout(component);
  const selectionProps = useComponentSelection({ component, componentPath });

  // --- Embedded resource ---
  const resource = component.EmbeddedResource ? bundle.EmbeddedResourceById[component.EmbeddedResource.Id] : undefined;
  if (component.EmbeddedResource && !resource) {
    logger.warn("Embedded resource not found:", component.EmbeddedResource.Id);
  }

  // --- ContentBase64 expression ---
  const [base64Value] = useObservedExpression<string>(component.ContentBase64, context, "ImageComponent");

  // --- ContentFileId expression → resolve URL via FileClient ---
  const [fileIdValue] = useObservedExpression<string>(component.ContentFileId, context, "ImageComponent");
  const mediaFileId = resource?.FileId;
  const mediaResourceId = resource?.Id;
  const resolvedFileId = fileIdValue || mediaFileId;
  const requestKey = JSON.stringify([resolvedFileId, fileIdValue, mediaFileId, mediaResourceId, bundle.Id, component.Id]);
  const [file, setFile] = useState<{ key: string; url?: string }>();
  const fileUrl = file?.key === requestKey ? file.url : undefined;

  useEffect(() => {
    if (!resolvedFileId) {
      return;
    }
    const controller = new AbortController();
    const pending = !fileIdValue && mediaFileId && bundle.Id && !IsDesignMode
      ? mediaClient.get(bundle.Id, mediaResourceId!, component.Id, controller.signal)
      : fileClient.getById(resolvedFileId, component.Id, controller.signal);
    pending.then((file) => { if (!controller.signal.aborted) setFile({ key: requestKey, url: file?.url }); })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setFile({ key: requestKey });
          errorHandler(error);
        }
      });
    return () => controller.abort();
  }, [resolvedFileId, fileIdValue, mediaFileId, mediaResourceId, bundle.Id, component.Id, fileClient, mediaClient, errorHandler, requestKey]);

  // Priority: ContentBase64 > ContentFileId > EmbeddedResource
  let src: string | undefined;
  if (base64Value) {
    src = base64Value.startsWith("data:") ? base64Value : `data:image/*;base64,${base64Value}`;
  } else if (fileUrl) {
    src = fileUrl;
  } else if (resource?.Content && !resource.FileId) {
    const mimeType = getMimeTypeFromName(resource.Name);
    src = `data:${mimeType};base64,${resource.Content}`;
  }

  const alt = component.Label ?? resource?.Name ?? "";

  return <ImageSurface key={src ?? requestKey} src={src} alt={alt} layout={componentLayoutSx} selection={selectionProps}
    pending={!!resolvedFileId && file?.key !== requestKey} />;
};

export default ImageComponent;
